<template>
  <div :id="`${type}-dropdown`" ref="dropdown" class="relative border-dashed bg-white">
    <div class="relative w-full">
      <!-- Filterable input -->
      <input
        v-if="filterable"
        :class="{
          ['rounded-b-none otivo-outline']: open,
          'bg-grey-4 border-grey-4 cursor-not-allowed': disabled,
          'text-red-dark enabled:otivo-outline-error': errorMessage,
          'border-grey-field enabled:hover:otivo-outline enabled:focus:otivo-outline enabled:active:otivo-outline':
            !errorMessage,
        }"
        :disabled="disabled"
        :placeholder="selected.label"
        :value="inputString"
        autocomplete="off"
        class="dropdown-filter flex justify-between py-[10px] px-[15px] border rounded-sm text-button-3 w-full placeholder-grey-1 placeholder:opacity-100"
        data-test="dropdown-filter"
        name="dropdown-filter"
        tabindex="0"
        type="text"
        @blur="handleBlur"
        @focus="
          () => {
            inputString = ''
            open = true
          }
        "
        @input="(event: Event) => filter((event.target as HTMLInputElement).value)"
        @keydown="handleKeydown" />

      <!-- Non-filterable display -->
      <div
        v-else
        :class="{
          ['rounded-b-none otivo-outline']: open,
          'bg-grey-4 border-grey-4 cursor-not-allowed': disabled,
          'text-red-dark enabled:otivo-outline-error': errorMessage,
          'border-grey-field enabled:hover:otivo-outline enabled:focus:otivo-outline enabled:active:otivo-outline':
            !errorMessage,
        }"
        class="dropdown-display flex justify-between py-[10px] px-[15px] border rounded-sm text-button-3 w-full cursor-pointer"
        data-test="dropdown-display"
        tabindex="0"
        @blur="handleBlur"
        @focus="
          () => {
            open = true
          }
        "
        @click="() => toggleOpen()"
        @keydown="handleKeydown">
        {{ selected.label }}
      </div>

      <Chevron
        class="absolute top-1/2 transform -translate-y-1/2 right-[15px]"
        :class="[{ 'pointer-events-none': !filterable }, { ['rotate-180']: open }]"
        fill="#696969"
        @click="
          () => {
            if (filterable) {
              inputString = ''
              open = true
            }
          }
        " />
    </div>
    <div
      v-if="open"
      class="absolute bg-white w-full border border-t-0 border-grey-field otivo-outline border-r-0 rounded rounded-t-none drop-shadow overflow-hidden z-10">
      <div
        v-if="filteredItems.length"
        class="max-h-[150px] overflow-auto"
        data-test="dropDownResults">
        <div
          v-for="(item, idx) in displayItems"
          :id="`item-${idx}`"
          :key="idx"
          :class="{ 'bg-blue-4': highlightedIndex === idx }"
          :data-test="`${type}-result-${sanitise((item.label as string).toLowerCase())}`"
          class="cursor-pointer p-4 text-14px font-medium hover:bg-blue-4 :active:otivo-outline :focus:otivo-outline focus:bg-otivo-4"
          tabindex="0"
          @mousedown.prevent="select(item)"
          @keydown.enter="select(item)">
          {{ item.label }}
        </div>
      </div>
      <div v-else class="flex justify-center pt-4 pb-4">
        <SyncLoader v-if="loading" color="#0064FF" />
        <div v-else class="font-medium">No results</div>
      </div>
    </div>
    <p v-if="errorMessage" class="otivo-error-message absolute right-0">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script lang="ts" setup>
/**
 * Dropdown:
 * @param type: string
 * @param items: InputItem[]
 * @param existingItem: InputItem | null
 */
import { computed, onMounted, ref, watchEffect } from 'vue'
import { sanitise } from '@/composables/sanitise'
import { onClickOutside } from '@vueuse/core'
import { InputItem } from '@/types/InputItem.ts'
import Chevron from '@/components/SVGS/Chevron.vue'
import SyncLoader from 'vue-spinner/src/SyncLoader.vue'

type DropdownProps = {
  type: string
  items: InputItem<string>[]
  existingItem: InputItem<string> | null
  filterable?: boolean
  defaultValue?: string
  placeholder?: string
  loading?: boolean
  disabled?: boolean
  errorMessage?: string
}

const props = withDefaults(defineProps<DropdownProps>(), {
  filterable: true,
  defaultValue: '',
  placeholder: 'Select an option',
  loading: false,
  disabled: false,
  errorMessage: '',
})
const emit = defineEmits({ selected: (item: InputItem<string>) => item })

const dropdown = ref(null)
const open = ref(false)
const inputString = ref(props.existingItem?.label || '')
const highlightedIndex = ref(-1)
const selected = ref<InputItem<string>>(getInitialSelection())

function getInitialSelection(): InputItem<string> {
  // Priority: existingItem > defaultValue match > placeholder
  if (props.existingItem) {
    return props.existingItem
  }

  if (props.defaultValue) {
    const defaultItem = props.items.find((item) => item.value === props.defaultValue)
    if (defaultItem) {
      return defaultItem
    }
  }

  return {
    label: props.placeholder,
    value: '',
  }
}

onClickOutside(dropdown, () => {
  open.value = false
})

onMounted(() => {
  if (props.existingItem) {
    selected.value = props.existingItem
    inputString.value = selected.value.label
  } else if (props.defaultValue && !props.filterable) {
    const defaultItem = props.items.find((item) => item.value === props.defaultValue)
    if (defaultItem) {
      selected.value = defaultItem
      inputString.value = defaultItem.label
    }
  }
})

const toggleOpen = () => {
  open.value = !open.value
}

const filter = (input: string) => {
  inputString.value = input
  highlightedIndex.value = -1 // Reset highlight when filtering
}

const BLUR_TIMEOUT_MS = 200

const handleBlur = () => {
  // Use setTimeout to allow mousedown events to be processed before closing
  setTimeout(() => {
    open.value = false
    highlightedIndex.value = -1
  }, BLUR_TIMEOUT_MS)
}

const handleKeydown = (event: KeyboardEvent) => {
  if (!open.value) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (props.filterable) {
        inputString.value = ''
      }
      open.value = true
      highlightedIndex.value = -1
    }
    return
  }

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      if (displayItems.value.length > 0) {
        highlightedIndex.value = Math.min(highlightedIndex.value + 1, displayItems.value.length - 1)
      }
      break
    case 'ArrowUp':
      event.preventDefault()
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0)
      break
    case 'Enter':
      event.preventDefault()
      if (highlightedIndex.value >= 0 && displayItems.value[highlightedIndex.value]) {
        select(displayItems.value[highlightedIndex.value])
      } else {
        open.value = false
      }
      break
    case 'Escape':
      event.preventDefault()
      open.value = false
      highlightedIndex.value = -1
      break
  }
}

const filteredItems = computed(() => {
  if (!props.filterable) {
    return props.items
  }
  return props.items.filter((item) => {
    const label = item.label as string
    return label.toLowerCase().includes(inputString.value.toLowerCase())
  })
})

const displayItems = computed(() => {
  return props.filterable ? filteredItems.value : props.items
})

const select = (item: InputItem<string>) => {
  selected.value = item
  inputString.value = item.label
  open.value = false
  emit('selected', item)
}

watchEffect(() => {
  if (!props.existingItem) {
    if (props.defaultValue && !props.filterable) {
      const defaultItem = props.items.find((item) => item.value === props.defaultValue)
      if (defaultItem) {
        selected.value = defaultItem
        inputString.value = defaultItem.label
        return
      }
    }
    inputString.value = props.filterable ? '' : selected.value.label
    selected.value = {
      label: props.placeholder,
      value: '',
    }
    return
  }

  if (props.existingItem !== selected.value) {
    selected.value = props.existingItem
    inputString.value = props.existingItem.label
  }
})
</script>

<style scoped>
.dropdown-display {
  outline: none;
}
</style>
